/**
 * Catalogue Pull - Vendor UI
 * 
 * Features:
 * - Connection Manager: Add/manage Shopify/WooCommerce stores
 * - Sync Controls: Manual sync, scheduled sync toggle
 * - Product Browser: View synced products, filter by status
 * - Category Mapping: Map unmapped categories
 * - Import: Convert external products to marketplace listings
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, Plus, RefreshCw, Settings, Trash2, ExternalLink, 
  AlertCircle, CheckCircle, Clock, Upload, Search, Filter,
  ChevronRight, Loader2, Link2, Unlink, Package, Tag, FolderTree
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import BulkCategoryAssignment from '../components/BulkCategoryAssignment';

// Platform logos/icons
const PLATFORM_CONFIG = {
  shopify: {
    name: 'Shopify',
    color: 'bg-green-500',
    description: 'Connect your Shopify store using Admin API access token',
    fields: {
      store_url: { label: 'Store URL', placeholder: 'mystore.myshopify.com', required: true },
      api_key: { label: 'Access Token', placeholder: 'shpat_xxxxx', required: true, type: 'password' }
    }
  },
  woocommerce: {
    name: 'WooCommerce',
    color: 'bg-purple-500',
    description: 'Connect your WooCommerce store using REST API credentials',
    fields: {
      store_url: { label: 'Store URL', placeholder: 'mystore.com', required: true },
      api_key: { label: 'Consumer Key', placeholder: 'ck_xxxxx', required: true },
      api_secret: { label: 'Consumer Secret', placeholder: 'cs_xxxxx', required: true, type: 'password' }
    }
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    connected: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Connected' },
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
    failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Failed' },
    expired: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: 'Expired' }
  };
  
  const { color, icon: Icon, label } = config[status] || config.pending;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

// Import status badge
const ImportStatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-gray-100 text-gray-800', label: 'Pending' },
    awaiting_category: { color: 'bg-yellow-100 text-yellow-800', label: 'Needs Category' },
    ready: { color: 'bg-blue-100 text-blue-800', label: 'Ready' },
    imported: { color: 'bg-green-100 text-green-800', label: 'Imported' },
    skipped: { color: 'bg-gray-100 text-gray-600', label: 'Skipped' },
    failed: { color: 'bg-red-100 text-red-800', label: 'Failed' }
  };
  
  const { color, label } = config[status] || config.pending;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
};

// Add Connection Modal
const AddConnectionModal = ({ isOpen, onClose, onSuccess, apiCall }) => {
  const [platform, setPlatform] = useState('shopify');
  const [formData, setFormData] = useState({
    store_name: '',
    store_url: '',
    api_key: '',
    api_secret: '',
    enable_scheduled_sync: false,
    sync_interval_hours: 24
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await apiCall('/api/catalogue-pull/connections', {
        method: 'POST',
        body: JSON.stringify({
          platform,
          ...formData
        })
      });
      
      if (response) {
        toast({ title: 'Success', description: 'Connection created successfully' });
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  const platformConfig = PLATFORM_CONFIG[platform];
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Add External Store</h2>
        
        {/* Platform Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Platform</label>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPlatform(key)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  platform === key 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-8 h-8 rounded ${config.color} mb-2`} />
                <div className="font-medium">{config.name}</div>
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-2">{platformConfig.description}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Connection Name</label>
            <Input
              value={formData.store_name}
              onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
              placeholder="My Shopify Store"
              required
            />
          </div>
          
          {Object.entries(platformConfig.fields).map(([key, field]) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-1">{field.label}</label>
              <Input
                type={field.type || 'text'}
                value={formData[key]}
                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                placeholder={field.placeholder}
                required={field.required}
              />
            </div>
          ))}
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="scheduled_sync"
              checked={formData.enable_scheduled_sync}
              onChange={(e) => setFormData({ ...formData, enable_scheduled_sync: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="scheduled_sync" className="text-sm">Enable scheduled sync</label>
          </div>
          
          {formData.enable_scheduled_sync && (
            <div>
              <label className="block text-sm font-medium mb-1">Sync Interval (hours)</label>
              <select
                value={formData.sync_interval_hours}
                onChange={(e) => setFormData({ ...formData, sync_interval_hours: parseInt(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value={1}>Every hour</option>
                <option value={6}>Every 6 hours</option>
                <option value={12}>Every 12 hours</option>
                <option value={24}>Daily</option>
              </select>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Connection
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Connection Card Component
const ConnectionCard = ({ connection, onRefresh, apiCall }) => {
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();
  
  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await apiCall(`/api/catalogue-pull/connections/${connection.id}/test`, {
        method: 'POST'
      });
      
      if (response?.success) {
        toast({ title: 'Success', description: response.message });
      } else {
        toast({ title: 'Connection Failed', description: response?.message, variant: 'destructive' });
      }
      onRefresh();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };
  
  const startSync = async () => {
    setSyncing(true);
    try {
      const response = await apiCall(`/api/catalogue-pull/connections/${connection.id}/sync`, {
        method: 'POST'
      });
      
      if (response?.success) {
        toast({ title: 'Sync Started', description: 'Products are being synced in the background' });
      }
      onRefresh();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };
  
  const deleteConnection = async () => {
    if (!window.confirm('Are you sure you want to delete this connection? All synced products will be removed.')) {
      return;
    }
    
    try {
      await apiCall(`/api/catalogue-pull/connections/${connection.id}`, {
        method: 'DELETE'
      });
      toast({ title: 'Success', description: 'Connection deleted' });
      onRefresh();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  const platformConfig = PLATFORM_CONFIG[connection.platform];
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-lg ${platformConfig?.color || 'bg-gray-500'} flex items-center justify-center`}>
            <Store className="h-6 w-6 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{connection.store_name}</h3>
              <StatusBadge status={connection.status} />
            </div>
            <p className="text-sm text-gray-500 truncate">{connection.store_url}</p>
            
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span>{connection.total_products_synced} products synced</span>
              <span>{connection.total_products_imported} imported</span>
              {connection.last_sync_at && (
                <span>Last sync: {new Date(connection.last_sync_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={testConnection}
              disabled={testing}
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={startSync}
              disabled={syncing || connection.status !== 'connected'}
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={deleteConnection}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {connection.error_message && (
          <div className="mt-3 p-2 bg-red-50 rounded text-sm text-red-700">
            {connection.error_message}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Products Tab Component
const ProductsTab = ({ connections, apiCall }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const { toast } = useToast();
  
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/catalogue-pull/products?page=${page}&limit=20`;
      if (selectedConnection) url += `&connection_id=${selectedConnection}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await apiCall(url);
      if (response) {
        setProducts(response.products || []);
        setTotalPages(response.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [page, selectedConnection, statusFilter, search]);
  
  const fetchCategories = async () => {
    try {
      const response = await apiCall('/api/categories');
      if (response) {
        setCategories(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts]);
  
  const handleImport = async () => {
    if (selectedProducts.length === 0) return;
    
    setImporting(true);
    try {
      const response = await apiCall('/api/catalogue-pull/products/import', {
        method: 'POST',
        body: JSON.stringify(selectedProducts)
      });
      
      if (response) {
        const successCount = response.success?.length || 0;
        const failedCount = response.failed?.length || 0;
        
        toast({
          title: 'Import Complete',
          description: `${successCount} products imported, ${failedCount} failed`
        });
        
        setSelectedProducts([]);
        fetchProducts();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };
  
  const updateCategory = async (productId, categoryId) => {
    try {
      await apiCall(`/api/catalogue-pull/products/${productId}/category?category_id=${categoryId}`, {
        method: 'PUT'
      });
      toast({ title: 'Success', description: 'Category updated' });
      fetchProducts();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search products..."
            className="pl-10"
          />
        </div>
        
        <select
          value={selectedConnection}
          onChange={(e) => { setSelectedConnection(e.target.value); setPage(1); }}
          className="border rounded-md px-3 py-2"
        >
          <option value="">All Stores</option>
          {connections.map(c => (
            <option key={c.id} value={c.id}>{c.store_name}</option>
          ))}
        </select>
        
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="border rounded-md px-3 py-2"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="awaiting_category">Needs Category</option>
          <option value="ready">Ready</option>
          <option value="imported">Imported</option>
        </select>
        
        {selectedProducts.length > 0 && (
          <Button onClick={handleImport} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Import ({selectedProducts.length})
          </Button>
        )}
      </div>
      
      {/* Products Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No products found</p>
          <p className="text-sm">Sync your external store to see products here</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === products.filter(p => p.import_status !== 'imported').length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProducts(products.filter(p => p.import_status !== 'imported').map(p => p.id));
                      } else {
                        setSelectedProducts([]);
                      }
                    }}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Product</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Price</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {products.map(product => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts([...selectedProducts, product.id]);
                        } else {
                          setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                        }
                      }}
                      disabled={product.import_status === 'imported'}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {product.image_urls?.[0] ? (
                        <img 
                          src={product.image_urls[0]} 
                          alt={product.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                          <Package className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-sm truncate max-w-[200px]">{product.title}</div>
                        <div className="text-xs text-gray-500">SKU: {product.sku || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {product.import_status === 'awaiting_category' ? (
                      <select
                        className="text-sm border rounded px-2 py-1"
                        defaultValue=""
                        onChange={(e) => updateCategory(product.id, e.target.value)}
                      >
                        <option value="" disabled>Select category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm">
                        {product.category_mapping?.suggested_category_name || product.external_category || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {product.price ? `₹${product.price.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <ImportStatusBadge status={product.import_status} />
                  </td>
                  <td className="px-4 py-3">
                    {product.external_url && (
                      <a 
                        href={product.external_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

// Main Component
const CataloguePull = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('connections');
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const fetchConnections = async () => {
    try {
      const response = await apiCall('/api/catalogue-pull/connections');
      if (response) {
        setConnections(response);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchConnections();
  }, []);
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Import Catalog</h1>
            <p className="text-gray-500 mt-1">Import products from your external stores</p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Store
          </Button>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="connections">
              <Store className="h-4 w-4 mr-2" />
              Connections
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="h-4 w-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="category-mapping" data-testid="category-mapping-tab">
              <FolderTree className="h-4 w-4 mr-2" />
              Bulk Category
            </TabsTrigger>
          </TabsList>
          
          {/* Connections Tab */}
          <TabsContent value="connections" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : connections.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Store className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">No stores connected</h3>
                  <p className="text-gray-500 mb-4">
                    Connect your Shopify or WooCommerce store to import products
                  </p>
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Store
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {connections.map(connection => (
                  <ConnectionCard 
                    key={connection.id} 
                    connection={connection} 
                    onRefresh={fetchConnections}
                    apiCall={apiCall}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Products Tab */}
          <TabsContent value="products" className="mt-6">
            <ProductsTab connections={connections} apiCall={apiCall} />
          </TabsContent>
          
          {/* Bulk Category Assignment Tab */}
          <TabsContent value="category-mapping" className="mt-6">
            <BulkCategoryAssignment 
              connectionId={null}
              onComplete={() => setActiveTab('products')}
            />
          </TabsContent>
        </Tabs>
        
        {/* Add Connection Modal */}
        <AddConnectionModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchConnections}
          apiCall={apiCall}
        />
      </div>
    </DashboardLayout>
  );
};

export default CataloguePull;
